/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import type { IObjectTreeIcon } from '../../../../../common/src/domain/object/IObjectTreeIcon';
import { ElementGroupNames } from '../../../../../common/src/enums/ElementGroupNames';
import { customInspectionTypeFrontEndSettings } from '../../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { Component } from '../../utils/Component';
import { ConfirmLink } from '../../utils/ConfirmLink';
import { ICategory } from '../Interfaces/ICategory';
import { IElement } from '../Interfaces/IElement';
import { IManifestation } from '../Interfaces/IManifestation';
import { IUnit } from '../Interfaces/IUnit';

interface IObjectTreeElementProps extends ITranslateProps {
    element: IElement;
    submitting: boolean;
    breakDownElementOpenState: { [key: string]: boolean };
    activeElementId?: undefined | string;
    activeUnitId?: undefined | string;
    activeManifestationId?: undefined | string;
    isUploading: boolean;
    onElementClicked: (element: IElement) => void;
    onUnitClicked: (unit: IUnit) => void;
    onManifestationClicked: (manifestation: IManifestation) => void;
}

class ObjectTreeElementComponent extends Component<IObjectTreeElementProps, Record<string, unknown>> {
    public override render() {
        return (
            <li key={this.props.element.id}>
                <ConfirmLink
                    className={classNames({
                        parent_link: true,
                        folding: this.props.element.units.length > 0 && this.props.breakDownElementOpenState[this.props.element.id],
                        unfolding: this.props.element.units.length > 0 && !this.props.breakDownElementOpenState[this.props.element.id],
                        'element-link': this.props.element.units.length > 0,
                        active: this.props.activeElementId === this.props.element.id && !this.props.activeUnitId,
                        elementIsArchived: this.props.element.isArchived,
                    })}
                    onClick={(e) => {
                        e.preventDefault();
                        this.props.onElementClicked(this.props.element);
                    }}
                    text={this.props.element.name + (this.props.element.location ? ` (${this.props.element.location})` : '')}
                    showConfirm={this.props.isUploading}
                    confirmText={this.props.t('survey.cancelUpload')}
                    icons={this.props.element.icons}
                />
                <ul>
                    {this.props.element.units.map((unit) => (
                        <li key={unit.id} className={this.props.breakDownElementOpenState[this.props.element.id] ? '' : 'hidden'}>
                            <ConfirmLink
                                className={classNames({
                                    'unit-link': unit.manifestations.length > 0,
                                    folding: unit.manifestations.length > 0 && this.props.breakDownElementOpenState[unit.id],
                                    unfolding: unit.manifestations.length > 0 && !this.props.breakDownElementOpenState[unit.id],
                                    active: this.props.activeUnitId === unit.id && !this.props.activeManifestationId,
                                    unitIsArchived: unit.isArchived || this.props.element.isArchived,
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.props.onUnitClicked(unit);
                                }}
                                text={unit.name + (unit.location ? ` (${unit.location})` : '')}
                                title={unit.material && unit.quantity ? ` ${unit.material} ${unit.quantity} ${unit.quantityUnitOfMeasurement} ` : ''}
                                showConfirm={this.props.isUploading}
                                confirmText={this.props.t('survey.cancelUpload')}
                                icons={unit.icons}
                            />
                            <ul>
                                {unit.manifestations.map((manifestation) => (
                                    <li key={manifestation.id} className={this.props.breakDownElementOpenState[unit.id] ? '' : 'hidden'}>
                                        <ConfirmLink
                                            className={classNames({
                                                'manifestation-link': true,
                                                active: this.props.activeManifestationId === manifestation.id,
                                                manifestationIsArchived: unit.isArchived || this.props.element.isArchived,
                                            })}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                this.props.onManifestationClicked(manifestation);
                                            }}
                                            text={manifestation.name + (manifestation.location ? ` (${manifestation.location})` : '')}
                                            showConfirm={this.props.isUploading}
                                            confirmText={this.props.t('survey.cancelUpload')}
                                            icons={manifestation.icons}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            </li>
        );
    }
}

const ObjectTreeElement = translate()(ObjectTreeElementComponent);

export interface IObjectTreeSelection {
    elementId?: string | undefined;
    unitId?: string | undefined;
    manifestationId?: string | undefined;
    code?: string | undefined;
    elementGroupName?: string | null | undefined;
    isArchiveSelected: boolean;
}

interface IObjectTreeProps extends ITranslateProps {
    categories: ICategory[];
    elements: IElement[];
    elementGroups: { elementGroupName: string; elements: IElement[]; icons?: IObjectTreeIcon[] }[];
    isUploading: boolean;
    isLoading: boolean;
    surveyId?: string;
    objectId: string;
    location: {
        query: {
            elementId: string;
            unitId: string;
            manifestationId: string;
            failureModeId?: string;
            filterOnElementGroups?: 'true';
            elementGroupName?: string;
        };
    };
    tab: string;
    showUtProjectsIcon?: boolean;
    selection: IObjectTreeSelection;
    options: {
        archiveLinkAvailable: boolean;
        hasElementsGroups: boolean;
        hideEditObjectBreakdownButton?: boolean;
        archiveMapName?: string;
        hiddeElements?: boolean;
    };
    filterOnElementGroups: boolean;
    inspectionStandardType?: string;
    onUpdateFilterOnElementGroups?(value: boolean): Promise<any>;
    onUpdateSelection(value: IObjectTreeSelection): any;
}

interface IObjectTreeState {
    breakDownTreeItemOpenState: { [key: string]: boolean };
    breakDownCategoryOpenState: { [key: string]: boolean };
    customInspectionStandardFilterOn: boolean;
}

class ObjectTree extends Component<IObjectTreeProps, IObjectTreeState> {
    constructor(props: IObjectTreeProps) {
        super(props);

        const breakDownTreeItemOpenState = {} as any;

        if (props.selection.elementId) {
            breakDownTreeItemOpenState[props.selection.elementId] = true;
        }
        if (props.selection.unitId) {
            breakDownTreeItemOpenState[props.selection.unitId] = true;
        }
        if (props.selection.manifestationId) {
            breakDownTreeItemOpenState[props.selection.manifestationId] = true;
        }
        if (!props.selection.elementId && !props.selection.unitId && !props.selection.manifestationId && props.options.archiveLinkAvailable) {
            this.onArchiveClicked();
        }

        const breakDownCategoryOpenState = {} as any;
        if (props.selection.elementGroupName) {
            breakDownCategoryOpenState[props.selection.elementGroupName] = true;
        }
        this.state = {
            breakDownTreeItemOpenState,
            breakDownCategoryOpenState,
            customInspectionStandardFilterOn: false,
        };
    }

    public override render() {
        return (
            <div id="object-tree">
                {this.props.options.hasElementsGroups && (
                    <>
                        <div className="checkbox ml10 mb20">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={this.props.filterOnElementGroups}
                                    onChange={async () => await this.props.onUpdateFilterOnElementGroups!(!this.props.filterOnElementGroups)}
                                />
                                {this.props.t('cra.filterConstructionElements')}
                            </label>
                        </div>
                    </>
                )}
                {customInspectionTypeFrontEndSettings[this.props.inspectionStandardType!]?.objectTreeFilter && (
                    <div className="checkbox ml10 mb20">
                        <label>
                            <input
                                type="checkbox"
                                checked={this.state.customInspectionStandardFilterOn}
                                onChange={async () =>
                                    await this.setState({ customInspectionStandardFilterOn: !this.state.customInspectionStandardFilterOn })
                                }
                            />
                            {this.props.t(customInspectionTypeFrontEndSettings[this.props.inspectionStandardType!]!.objectTreeFilter!.translationKey)}
                        </label>
                    </div>
                )}

                {this.props.categories && this.props.categories.length === 0 && (
                    <p className="mb20">
                        <a
                            href="#"
                            id="unfold_all"
                            className="bt btn-sm fw600"
                            onClick={(e) => {
                                e.preventDefault();
                                this.onExpandAll();
                            }}
                        >
                            <span className="fa fa fa-plus-square-o"></span> {this.props.t('survey.expandAll')}
                        </a>
                        <a
                            href="#"
                            id="fold_all"
                            className="bt btn-sm fw600"
                            onClick={(e) => {
                                e.preventDefault();
                                this.onCollapseAll();
                            }}
                        >
                            <span className="fa fa-minus-square-o pl5"></span> {this.props.t('survey.collapseAll')}
                        </a>
                    </p>
                )}

                {this.props.categories && !this.props.options.hiddeElements && (
                    <ul key="objecttree">
                        {!this.props.filterOnElementGroups &&
                            this.props.categories &&
                            this.props.categories.length > 0 &&
                            this.props.elements.length > 0 &&
                            this.props.categories.map((category) => [
                                <li key={category.id} className="mb15">
                                    <ConfirmLink
                                        className={classNames({
                                            parent_link: true,
                                            folding: this.state.breakDownCategoryOpenState[category.id],
                                            unfolding: !this.state.breakDownCategoryOpenState[category.id],
                                            'element-link': true,
                                        })}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this.onCategoryClicked(category.id);
                                        }}
                                        text={category.name ? category.name : 'Unknown'}
                                        showConfirm={this.props.isUploading}
                                        confirmText={this.props.t('survey.cancelUpload')}
                                    />

                                    <ul>
                                        {this.state.breakDownCategoryOpenState[category.id] &&
                                            this.getFilteredElements()
                                                .filter((element: any) => element.categoryId === category.id)
                                                .map((element, i) => [
                                                    <ObjectTreeElement
                                                        key={i}
                                                        element={element}
                                                        submitting={this.props.isLoading}
                                                        breakDownElementOpenState={this.state.breakDownTreeItemOpenState}
                                                        activeElementId={this.props.selection.elementId}
                                                        activeUnitId={this.props.selection.unitId}
                                                        activeManifestationId={this.props.selection.manifestationId}
                                                        isUploading={this.props.isUploading}
                                                        onElementClicked={(clickedElement) => this.onElementClicked(clickedElement)}
                                                        onUnitClicked={(clickedUnit) => this.onUnitClicked(clickedUnit)}
                                                        onManifestationClicked={(manifestation) => this.onManifestationClicked(manifestation)}
                                                    />,
                                                ])}
                                    </ul>
                                </li>,
                            ])}
                        {this.props.filterOnElementGroups &&
                            [ElementGroupNames.Substructure, ElementGroupNames.Superstructure, ElementGroupNames.Foundation].map(
                                (elementGroupName, groupIndex) => [
                                    <li key={groupIndex} className="mb15">
                                        <ConfirmLink
                                            className={classNames({
                                                parent_link: true,
                                                folding: this.state.breakDownCategoryOpenState[elementGroupName],
                                                unfolding: !this.state.breakDownCategoryOpenState[elementGroupName],
                                                'element-link': true,
                                                active: this.props.selection.elementGroupName === elementGroupName && !this.props.selection.elementId,
                                            })}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                this.onElementGroupNameClicked(elementGroupName);
                                            }}
                                            text={this.props.t(`elementGroupName.${elementGroupName}`)}
                                            showConfirm={this.props.isUploading}
                                            confirmText={this.props.t('survey.cancelUpload')}
                                            icons={this.props.elementGroups.find((i) => i.elementGroupName === elementGroupName)?.icons}
                                        />

                                        <ul>
                                            {this.state.breakDownCategoryOpenState[elementGroupName] &&
                                                this.getFilteredElements()
                                                    .filter((element: any) => element.elementGroupName === elementGroupName)
                                                    .map((element, i) => [
                                                        <ObjectTreeElement
                                                            key={i}
                                                            element={element}
                                                            submitting={this.props.isLoading}
                                                            breakDownElementOpenState={this.state.breakDownTreeItemOpenState}
                                                            activeElementId={this.props.selection.elementId}
                                                            activeUnitId={this.props.selection.unitId}
                                                            activeManifestationId={this.props.selection.manifestationId}
                                                            isUploading={this.props.isUploading}
                                                            onElementClicked={(clickedElement) => this.onElementClicked(clickedElement)}
                                                            onUnitClicked={(clickedUnit) => this.onUnitClicked(clickedUnit)}
                                                            onManifestationClicked={(manifestation) => this.onManifestationClicked(manifestation)}
                                                        />,
                                                    ])}
                                        </ul>
                                    </li>,
                                ],
                            )}
                        {(!this.props.categories || this.props.categories.length === 0) &&
                            !this.props.filterOnElementGroups &&
                            this.props.elements.length > 0 && (
                                <>
                                    {this.getFilteredElements().map((element, j) => [
                                        <ObjectTreeElement
                                            key={j}
                                            element={element}
                                            submitting={this.props.isLoading}
                                            breakDownElementOpenState={this.state.breakDownTreeItemOpenState}
                                            activeElementId={this.props.selection.elementId}
                                            activeUnitId={this.props.selection.unitId}
                                            activeManifestationId={this.props.selection.manifestationId}
                                            isUploading={this.props.isUploading}
                                            onElementClicked={(clickedElement) => this.onElementClicked(clickedElement)}
                                            onUnitClicked={(clickedUnit) => this.onUnitClicked(clickedUnit)}
                                            onManifestationClicked={(manifestation) => this.onManifestationClicked(manifestation)}
                                        />,
                                    ])}
                                </>
                            )}
                    </ul>
                )}
                {this.props.options.archiveLinkAvailable && (
                    <ul key="archivelink">
                        <li>
                            <span className="fa fa-archive pr5"></span>

                            <ConfirmLink
                                className={classNames({ active: this.props.selection.isArchiveSelected })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.onArchiveClicked();
                                }}
                                text={this.props.options.archiveMapName ? this.props.options.archiveMapName : this.props.t('common.archive')}
                                showConfirm={this.props.isUploading}
                                confirmText={this.props.t('survey.cancelUpload')}
                            />
                        </li>
                    </ul>
                )}
                {!this.props.options.hideEditObjectBreakdownButton && (
                    <Link
                        to={`/objects/${this.props.objectId}/surveyeditcomposition?surveyid=${this.props.surveyId}&tab=${this.props.tab}`}
                        className="btn btn-default btn-sm fw600 ml10 mt20"
                    >
                        <span className="fa fa-edit pr5"></span> {this.props.t('common.editItem', this.props.t('object.objectBreakdown'))}
                    </Link>
                )}
            </div>
        );
    }

    private getFilteredElements() {
        if (this.state.customInspectionStandardFilterOn) {
            const filters = customInspectionTypeFrontEndSettings[this.props.inspectionStandardType!]!.objectTreeFilter!;

            const filteredElements: IElement[] = [];

            for (const element of this.props.elements) {
                if (filters.elementFilter === undefined || filters.elementFilter(element)) {
                    const elementToAdd = { ...element };
                    elementToAdd.units = [];
                    for (const unit of element.units) {
                        if (filters.unitFilter === undefined || filters.unitFilter(unit)) {
                            elementToAdd.units.push(unit);
                        }
                    }

                    filteredElements.push(elementToAdd);
                }
            }
            return filteredElements;
        }
        return this.props.elements;
    }

    private onExpandAll() {
        const breakDownCategoryOpenState = {} as { [key: string]: boolean };
        const breakDownTreeItemOpenState = {} as { [key: string]: boolean };
        for (const element of this.props.elements) {
            breakDownCategoryOpenState[element.id] = true;
            breakDownTreeItemOpenState[element.id] = true;
            for (const unit of element.units) {
                breakDownCategoryOpenState[unit.id] = true;
                breakDownTreeItemOpenState[unit.id] = true;
                for (const manifestation of unit.manifestations) {
                    breakDownCategoryOpenState[manifestation.id] = true;
                    breakDownTreeItemOpenState[manifestation.id] = true;
                }
            }
        }

        breakDownCategoryOpenState[ElementGroupNames.Substructure] = true;
        breakDownCategoryOpenState[ElementGroupNames.Superstructure] = true;
        breakDownCategoryOpenState[ElementGroupNames.Foundation] = true;

        this.setState({ breakDownCategoryOpenState, breakDownTreeItemOpenState });
    }

    private onCollapseAll() {
        this.setState({ breakDownCategoryOpenState: {}, breakDownTreeItemOpenState: {} });
    }

    private async onCategoryClicked(categoryId: string) {
        const { breakDownCategoryOpenState } = this.state;
        breakDownCategoryOpenState[categoryId] = !breakDownCategoryOpenState[categoryId];
        await this.setState({ breakDownCategoryOpenState });
    }

    private async onElementGroupNameClicked(elementGroupName: string) {
        const { breakDownCategoryOpenState } = this.state;
        breakDownCategoryOpenState[elementGroupName] = !breakDownCategoryOpenState[elementGroupName];

        await this.setState({ breakDownCategoryOpenState });

        await this.props.onUpdateSelection({
            elementId: undefined,
            code: undefined,
            elementGroupName,
            unitId: undefined,
            manifestationId: undefined,
            isArchiveSelected: false,
        });
    }

    private async onElementClicked(element: IElement) {
        const { breakDownTreeItemOpenState } = this.state;
        breakDownTreeItemOpenState[element.id] = !breakDownTreeItemOpenState[element.id];
        await this.setState({ breakDownTreeItemOpenState });

        await this.props.onUpdateSelection({
            elementId: element.id,
            code: element.code,
            elementGroupName: element.elementGroupName,
            unitId: undefined,
            manifestationId: undefined,
            isArchiveSelected: false,
        });
    }

    private async onUnitClicked(unit: IUnit) {
        const { breakDownTreeItemOpenState } = this.state;
        breakDownTreeItemOpenState[unit.id] = !breakDownTreeItemOpenState[unit.id];
        await this.setState({ breakDownTreeItemOpenState });

        await this.props.onUpdateSelection({
            elementGroupName: this.props.selection.elementGroupName,
            elementId: unit.elementId,
            code: unit.code,
            unitId: unit.id,
            manifestationId: undefined,
            isArchiveSelected: false,
        });
    }

    private async onManifestationClicked(manifestation: IManifestation) {
        await this.props.onUpdateSelection({
            elementGroupName: this.props.selection.elementGroupName,
            elementId: manifestation.elementId,
            code: manifestation.code,
            unitId: manifestation.unitId,
            manifestationId: manifestation.id,
            isArchiveSelected: false,
        });
    }

    private async onArchiveClicked() {
        await this.props.onUpdateSelection({
            elementId: undefined,
            code: undefined,
            elementGroupName: undefined,
            unitId: undefined,
            manifestationId: undefined,
            isArchiveSelected: true,
        });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(ObjectTree);
